import jsPDF from 'jspdf';
import dayjs from 'dayjs';

/** Loan-page values the payoff quote needs, passed in by the Promise to Pay panel. */
export interface PayoffContext {
	loanGroupNo: number | null;
	/** Insurance premium + tax, from the loan page meta. */
	insurance: number;
	/** NSF fees balance (`eff_fees_balance`), from the loan page meta. */
	nsfFees: number;
	originationDate: string | null;
}

/** NLS payoff-details payload (`/getPayoffInfo`). */
export interface PayoffInfo {
	PrincipalBalance: number;
	AccruedInterest: number;
	PerDiem: number;
	PayoffAmount: number;
}

/** Row from `/getPayoffStatment`. */
export interface PayoffStatementInfo {
	loan_number: string;
	curr_maturity_date: string;
	'Debtor Address': string;
	'Debtor City': string;
	'Debtor Province': string;
	'Debtor Postal Code': string;
}

/** Row from `/payableBankingAccount/:loanGroupNo`. */
export interface PayableAccount {
	payoff_email: string;
	direct_deposit_name: string;
	institution_number: string;
	institution_name: string;
	transit_number: string;
	account_no: string;
}

export interface PayoffQuote {
	payoffDate: string;
	/** Pay Off includes NSF fees; Pay Off (exclude NSF) takes them off. */
	includeNsf: boolean;
	info: PayoffInfo;
	statement: PayoffStatementInfo;
	payable: PayableAccount | null;
	insurance: number;
	nsfFees: number;
	originationDate: string | null;
	/** Amount filled into the form. */
	amount: number;
}

/** Same calculation as the legacy arrangement form. */
export function payoffAmount(
	info: PayoffInfo,
	includeNsf: boolean,
	nsfFees: number
): number {
	return includeNsf
		? info.PayoffAmount
		: Number((info.PayoffAmount - nsfFees).toFixed(2));
}

/** Builds the payoff statement letter. Content and layout match the legacy form. */
export function buildPayoffStatementPdf(
	quote: PayoffQuote,
	customerName: string
): jsPDF {
	const { info, statement, payable, includeNsf } = quote;
	const doc = new jsPDF('portrait', 'px', 'a4');

	doc.setFontSize(12);
	doc.setFont('Roman', 'normal');
	doc.text(dayjs().format('YYYY-MM-DD'), 40, 45);
	doc.text(customerName, 40, 65);
	doc.text(
		statement['Debtor Address'].concat(' ').concat(statement['Debtor City']),
		40,
		75
	);
	doc.text(
		statement['Debtor Province']
			.concat(' ')
			.concat(statement['Debtor Postal Code']),
		40,
		85
	);
	doc.text('Dear ' + customerName + ',', 40, 105);
	doc.text(
		'The following table is a breakdown of the funds required to pay off your loan. You are authorized to use',
		40,
		125
	);
	doc.text(
		'the following amounts to pay off the above-mentioned loan. All necessary legal documents will be',
		40,
		135
	);
	doc.text(
		'forwarded to the trustee for full reconveyance upon receipt of payment in full.',
		40,
		145
	);
	doc.text('Account Number: ' + statement.loan_number, 40, 165);
	doc.text('Loan Date: ' + quote.originationDate, 40, 185);
	doc.text('Maturity Date: ' + statement.curr_maturity_date, 40, 205);
	doc.text('Payoff Date: ' + quote.payoffDate, 40, 225);
	doc.text('Principal Balance: $' + info.PrincipalBalance, 40, 245);
	doc.text('Accrued Interest: $' + info.AccruedInterest?.toFixed(2), 40, 265);
	doc.text('Insurance: $' + quote.insurance, 40, 285);
	if (includeNsf) {
		doc.text('NSF Fees: $' + quote.nsfFees, 40, 305);
	}
	doc.text(
		'Per Diem: $' + info.PerDiem?.toFixed(2),
		40,
		includeNsf ? 325 : 305
	);
	doc.setFont('Roman', 'bold');
	doc.text(
		'Payoff Amount: $' +
			(includeNsf
				? info.PayoffAmount
				: (info.PayoffAmount - quote.nsfFees).toFixed(2)),
		40,
		includeNsf ? 345 : 325
	);

	doc.setFont('Roman', 'normal');
	doc.text('Payment options include: ', 40, 365);
	doc.setFont('Roman', 'bold');
	doc.text('--written consent by mail or email', 40, 385);
	doc.setFont('Roman', 'normal');
	doc.text(
		'to customerservice@lmcredit.com authorizing LM CREDIT Inc.',
		174,
		385
	);
	doc.text(
		'to debit this outstanding balance from the preauthorized account on file.',
		40,
		395
	);
	doc.setFont('Roman', 'bold');
	doc.text('--e-transfer', 40, 405);
	doc.setFont('Roman', 'normal');
	doc.text(`to ${payable?.payoff_email}. `, 87, 405);
	doc.setFont('Roman', 'bold');
	// Spacing follows the length of the email address.
	doc.text(
		'No security answer is required.',
		(payable?.payoff_email?.length ?? 0) <= 21 ? 190 : 203,
		405
	);
	doc.text('PAYABLE TO: ' + payable?.direct_deposit_name, 40, 425);
	doc.text('DIRECT DEPOSIT TO: ', 40, 435);
	doc.text(
		`INSTITUTION NUMBER: ${payable?.institution_number} (${payable?.institution_name})`,
		40,
		445
	);
	doc.text('TRANSIT NUMBER: ' + payable?.transit_number, 40, 455);
	doc.text('ACCOUNT NUMBER: ' + payable?.account_no, 40, 465);
	doc.setFont('Roman', 'normal');
	doc.text(
		'To ensure your payment is processed in a timely manner,',
		40,
		485
	);
	doc.setFont('Roman', 'bold');
	doc.setTextColor(255, 0, 0);
	doc.text('please send a copy of the receipt to', 248, 485);
	doc.text('payments@lmcredit.com', 40, 495);
	doc.setFont('Roman', 'normal');
	doc.setTextColor(0, 0, 0);
	doc.text(
		'. Once the receipt is received, the payment will be processed and applied to',
		136,
		495
	);
	doc.text('the account accordingly.', 40, 505);
	doc.text(
		'If payment is made via certified cheque or bank draft, please include a picture of the item along with the ',
		40,
		525
	);
	doc.text('picture of the deposit receipt.', 40, 535);
	doc.text(
		'We reserve the right to amend this statement should any changes occur that would increase the total',
		40,
		555
	);
	doc.text('amount for payoff.', 40, 565);
	doc.text('Thank you,', 40, 585);
	doc.text('LM CREDIT INC.', 40, 605);
	doc.setFontSize(9);
	doc.setFont('Roman', 'italic');
	doc.text(
		'T: 1-833-301-1058  F: 1-866-509-0341  A: 1 CITY CENTRE DRIVE, UNIT 801, MISSISSAUGA, ON, L5B 1M2',
		40,
		625
	);
	return doc;
}
